// Centralized API Configuration
// This file helps manage the backend URL and makes it easier to switch between environments

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Default API URL - you can change this to match your backend port
const DEFAULT_API_URL = 'http://localhost:5002/api';

// Alternative ports to try if the default doesn't work
const ALTERNATIVE_PORTS = [5001, 3001, 8000, 5000];

// Get API URL from environment variables or use default
export const API_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

// Function to test API connectivity
export const testApiConnection = async () => {
  // Try to connect to the base API URL - if server is running, it should respond
  const testUrl = API_URL;
  
  try {
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // If we get any response (even 404), the server is running
    console.log('✅ API connection successful:', API_URL);
    return true;
  } catch (error) {
    // Only treat network errors as connection failures
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      console.log('❌ API connection failed - server not running:', error.message);
      return false;
    } else {
      // Other errors (like 404) mean server is running but endpoint doesn't exist
      console.log('⚠️ API server is running but endpoint not found:', error.message);
      return true;
    }
  }
};

// Alternative simple connection test
export const simpleConnectionTest = async () => {
  const baseUrl = API_URL.replace('/api', '');
  
  try {
    // Try to connect to the base server URL (without /api)
    const response = await fetch(baseUrl, {
      method: 'GET',
      mode: 'no-cors' // This will work even if CORS is not configured
    });
    
    console.log('✅ Server is reachable:', baseUrl);
    return true;
  } catch (error) {
    console.log('❌ Server not reachable:', error.message);
    return false;
  }
};

// Function to find working API URL
export const findWorkingApiUrl = async () => {
  console.log('🔍 Testing API connectivity...');
  
  // First try the configured URL
  if (await testApiConnection()) {
    return API_URL;
  }
  
  // Try alternative ports
  for (const port of ALTERNATIVE_PORTS) {
    const testUrl = `http://localhost:${port}/api`;
    console.log(`🔍 Trying port ${port}...`);
    
    try {
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // If we get any response, the server is running on this port
      console.log(`✅ Found working API on port ${port}:`, testUrl);
      return testUrl;
    } catch (error) {
      // Only treat network errors as connection failures
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.log(`❌ Port ${port} failed - server not running:`, error.message);
      } else {
        // Other errors mean server is running but endpoint doesn't exist
        console.log(`✅ Found server on port ${port} (endpoint not found):`, testUrl);
        return testUrl;
      }
    }
  }
  
  console.log('❌ No working API found. Please check if your backend server is running.');
  return API_URL; // Return default even if it doesn't work
};

// Export the current API URL
export default API_URL;
